import { IMAGES } from "@/assets/images";
import { useAuthHelper } from "@/components/auth/helper";
import ImageApp from "@/components/ui/image-app";
import { APP_SUPPORT_DOMAIN } from "@/config/constants";
import { routes } from "@/config/routes";
import useProfile from "@/hooks/use-profile";
import { MAPPING_NAME } from "@/utilities/functions/ldp-options";
import {
  E_LANDING_PAGE_TYPE,
  ILandingPage,
} from "@/utilities/types/LandingPage";
import { Roles } from "@/utilities/types/Users";
import React, { useCallback, useMemo, useRef } from "react";
import toast, { Toast } from "react-hot-toast";
import { BsEye } from "react-icons/bs";
import { Link } from "react-router-dom";
import { Badge, Button, Text, Title } from "rizzui";
import { CustomizeDomainForm } from "../create-update/DomainConfigModal";
import cn from "@/utils/class-names";
import { CustomizeDomainState, ProjectLandingGridListingStates } from "./types";
import { DropdownActions } from "./DropdownActions";

interface ProjectGridCardProps {
  ldpData: ILandingPage;
  onDeleteItem?: (id: string) => Promise<void>;
  onReplaceLatestContent?: (id: string) => Promise<void>;
  handleDomainModal?: (
    data: Partial<CustomizeDomainForm> & CustomizeDomainState
  ) => void;
  handleOptionModal?: (
    data: ProjectLandingGridListingStates["modifyModal"]
  ) => void;
  handleScriptsModal?: (
    data: ProjectLandingGridListingStates["scriptsModal"]
  ) => void;
}

const ProjectGridCard = React.memo(
  ({
    ldpData,
    onDeleteItem,
    handleDomainModal,
    handleOptionModal,
    handleScriptsModal,
    onReplaceLatestContent,
  }: ProjectGridCardProps) => {
    const {
      id,
      user,
      project,
      optionMappings,
      headerScript,
      bodyScript,
      domain, // Active domain,
      type,
    } = ldpData;
    const { profile } = useProfile();

    const isAdmin = profile?.role === Roles.ADMIN;
    const agentCanEdit = !ldpData?.useProjectSgTrackingId;
    const { shareToken } = useAuthHelper();

    const toasterRef = useRef<Toast>(null);

    const isSetupScript = useMemo(() => {
      return !!headerScript || !!bodyScript;
    }, [headerScript, bodyScript]);
    const currentActiveOption =
      optionMappings.find((op) => op?.active)?.landingPageOption?.name || "O1";
    const mappingOptionName = MAPPING_NAME[currentActiveOption];
    const originalDomain = user?.domains?.find((d) => d?.primary)?.name;
    const aliasEditDynamic = `https://${APP_SUPPORT_DOMAIN}/editor/landing-page`;

    const handleDelete = useCallback(() => {
      toast(
        (t) => {
          toasterRef.current = t;
          return (
            <div className="w-[500px] pb-2 pt-1 text-left rtl:text-right">
              <Title
                as="h6"
                className="mb-0.5 flex items-start text-xs text-gray-700 sm:items-center"
              >
                Delete Landing Page
              </Title>
              <Text className="mb-2 leading-relaxed text-gray-500">
                Are you sure to delete this Landing Page
              </Text>
              <div>
                <Button
                  size="sm"
                  className="me-1.5 h-7"
                  type="submit"
                  onClick={async () => {
                    try {
                      await onDeleteItem?.(id);
                    } finally {
                      toast.dismiss(t.id);
                    }
                  }}
                >
                  Yes
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  onClick={() => toast.dismiss(t.id)}
                >
                  No
                </Button>
              </div>
            </div>
          );
        },
        { position: "top-center" }
      );
    }, [id, onDeleteItem]);

    const handleReplaceLatestContent = useCallback(() => {
      toast(
        (t) => {
          // @ts-ignore
          toasterRef.current = t;
          return (
            <div className="w-56 pb-2 pt-1 text-left rtl:text-right">
              <Title
                as="h6"
                className="mb-0.5 flex items-start text-xs text-gray-700 sm:items-center"
              >
                Replace latest content
              </Title>
              <Text className="mb-2 leading-relaxed text-gray-500">
                This action will replace all the layout configuration of the
                Landing Page with the latest content of the project and cannot
                be undone. Are you sure to proceed?
              </Text>
              <div className="flex items-center justify-end">
                <Button
                  size="sm"
                  className="me-1.5 h-7"
                  type="submit"
                  onClick={async () => {
                    try {
                      await onReplaceLatestContent?.(id);
                    } finally {
                      toast.dismiss(t.id);
                    }
                  }}
                >
                  Yes
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7"
                  onClick={() => toast.dismiss(t.id)}
                >
                  No
                </Button>
              </div>
            </div>
          );
        },
        { position: "top-right" }
      );
    }, [id, onReplaceLatestContent]);

    const handleEdit = useCallback(async () => {
      if (APP_SUPPORT_DOMAIN) {
        await shareToken?.({
          ldpConfig: {
            domain: domain,
            backToUrl: window.location.href,
            originalDomain: originalDomain || "",
          },
        });
        window.location.href = aliasEditDynamic;
      }
    }, [shareToken, aliasEditDynamic, originalDomain, domain]);

    const thumbnail = useMemo(() => {
      return (
        project?.photo?.urls?.[0]?.url || project?.medias?.[0]?.urls?.[0]?.url
      );
    }, [project]);

    return (
      <div className="flex flex-col h-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg group cursor-pointer">
        {/* Image Section */}
        <div className="relative aspect-square w-full flex-shrink-0 overflow-hidden">
          <ImageApp
            inApp={!thumbnail}
            className="object-cover w-full h-full opacity-75 group-hover:opacity-100 transition-opacity"
            src={thumbnail || IMAGES.PROJ_IMAGE_HOLDER}
            fallback={IMAGES.PROJ_IMAGE_HOLDER}
          />
          <Badge rounded="md" className="absolute top-2 left-2 z-10">
            {mappingOptionName}
          </Badge>

          <Badge
            rounded="md"
            className="absolute bottom-2 right-2 z-10 capitalize"
            color="info"
          >
            {type}
          </Badge>

          <Link
            className="opacity-0 group-hover:opacity-100 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity"
            target="_blank"
            to={`https://${domain}`}
          >
            <Button size="sm">
              <BsEye className="text-white me-2" />
              <span>View</span>
            </Button>
          </Link>

          <DropdownActions
            ldpData={ldpData}
            isAdmin={isAdmin}
            agentCanEdit={agentCanEdit}
            isSetupScript={isSetupScript}
            handleDomainModal={handleDomainModal}
            handleEdit={handleEdit}
            handleScriptsModal={handleScriptsModal}
            handleOptionModal={handleOptionModal}
            handleDelete={handleDelete}
            handleReplaceLatestContent={handleReplaceLatestContent}
          />
        </div>

        {/* Info Section */}
        <div className="flex-1 p-4 text-xs flex flex-col justify-between">
          <div className="flex flex-col justify-between flex-1 gap-1">
            <div className="flex text-sm items-baseline gap-1">
              <span className="text-gray-400 font-bold">Project:</span>
              <div className="flex flex-row items-center gap-x-2">
                <Link
                  to={
                    profile?.role === Roles.ADMIN
                      ? routes.project.edit(project?.id || "")
                      : `https://${domain}`
                  }
                  className="font-bold  underline"
                >
                  {(project?.name as string) || "N/A"}
                </Link>

                <span
                  className={cn(
                    "aspect-square size-[5px] rounded-full animate-pulse",
                    isSetupScript && "bg-emerald-500",
                    !isSetupScript && "bg-red-500"
                  )}
                />
              </div>
            </div>

            <div className="flex gap-1">
              <span className="font-medium text-gray-400">Status:</span>
              <span className="font-medium">
                {user?.statusAccount === "inactive" ? "Draft" : "Published"}
              </span>
            </div>

            {isAdmin && (
              <div className="flex gap-1">
                <span className="font-medium text-gray-400">User:</span>
                <span className="font-medium">
                  {user
                    ? `${user?.firstName} ${user?.lastName} (${user?.email})`
                    : "N/A"}
                </span>
              </div>
            )}

            <div className="flex flex-wrap gap-1">
              <span className="text-gray-400">Domain:</span>
              <p
                style={{
                  overflowWrap: "anywhere",
                }}
                className=""
              >
                {domain || "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ProjectGridCard.displayName = "ProjectGridCard";

export default ProjectGridCard;

export const ProjectGridCardSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-lg overflow-hidden shadow-lg group cursor-pointer min-h-0 animate-pulse">
      {/* Image Section Skeleton */}
      <div className="relative aspect-square w-full flex-shrink-0 overflow-hidden">
        <div className="w-full h-full bg-gray-200" />
        {/* Badge Skeleton */}
        <div className="absolute top-2 left-2 z-10 h-6 w-16 bg-gray-300 rounded-md" />
        {/* Button Skeleton */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-20 bg-gray-300 rounded-md" />
        {/* Dropdown Skeleton */}
        <div className="absolute right-2 top-2 h-8 w-8 bg-gray-300 rounded-md" />
      </div>

      {/* Info Section Skeleton */}
      <div className="flex-1 p-4 text-xs flex flex-col justify-between min-h-0">
        <div className="flex flex-col justify-between gap-2">
          {/* Name Skeleton */}
          <div className="flex gap-1">
            <span className="font-medium h-4 w-16 bg-gray-300 rounded" />
            <span className="font-medium h-4 w-3/4 bg-gray-300 rounded" />
          </div>
          {/* User Skeleton */}
          <div className="flex gap-1">
            <span className="font-medium h-4 w-16 bg-gray-300 rounded" />
            <span className="font-medium h-4 w-1/2 bg-gray-300 rounded" />
          </div>
          {/* Email Skeleton */}
          <div className="flex flex-wrap gap-1">
            <span className="font-medium h-4 w-16 bg-gray-300 rounded" />
            <span className="h-4 w-2/3 bg-gray-300 rounded" />
          </div>
          {/* Original Domain Skeleton */}
          <div className="flex flex-wrap gap-1">
            <span className="h-4 w-24 bg-gray-300 rounded" />
            <span className="h-4 w-1/2 bg-gray-300 rounded" />
          </div>
          {/* Project Skeleton */}
          <div className="flex gap-1">
            <span className="h-4 w-16 bg-gray-300 rounded" />
            <span className="h-4 w-1/3 bg-gray-300 rounded" />
          </div>
        </div>
      </div>
    </div>
  );
};
